import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { ENodeTypes, IMinimalApi, ReadLaterLeaf, TDocumentTree } from "../types";

export const CACHE_KEYS = {
  INDEX_BY_YEAR: 'index-by-year',
  BOOKMARKS: 'bookmarks',
  INDEX_BY_MONITOR: 'index-by-monitor',
  READ_LATER: 'read-later',
  LATEST_DOCS: 'latest-docs',
} as const;

type CacheKeys = typeof CACHE_KEYS;
type CacheKeyValues = CacheKeys[keyof CacheKeys];

export type TCacheData = {
  data: any,
  isFetching?: boolean,
  isLoading?: boolean,
  expiresAt?: dayjs.Dayjs,
}

const defaultCacheExpirationMs = 60 * 60 * 1000; // 1 hour

const isDocInList = (docid: string, tree: TDocumentTree): boolean => {
  for (const entry of tree) {
    switch (entry.type) {
      case ENodeTypes.LEAF:
        if (entry.data._id === docid) {
          return true;
        }
        break;
      case ENodeTypes.NODE:
      if(isDocInList(docid, entry.children)) return true;
    }
  }
  return false;
}

export const useCache = (api: IMinimalApi) => {
  const [cache, setCache] = useState<Record<string, TCacheData>>(Object.values(CACHE_KEYS).reduce((acc: Record<string, TCacheData>, curr) => { acc[curr] = { data: null, isFetching: true, isLoading: true }; return acc; }, {}));

  const fetchers = useMemo(() => ({
    [CACHE_KEYS.INDEX_BY_YEAR]: api.loadIndexByYearAndTypes,
    [CACHE_KEYS.INDEX_BY_MONITOR]: api.loadIndexByYearAndMonitorNumber,
    [CACHE_KEYS.READ_LATER]: api.loadReadLaterDocs,
    [CACHE_KEYS.BOOKMARKS]: api.loadBookmarkedDocs,
    [CACHE_KEYS.LATEST_DOCS]: api.getLatestDocs,
  }), [api])

  const updateCache = useCallback((key: string, data: any, expiresInMilliseconds?: number) => {
    setCache(prev => ({
      ...prev,
      [key]: {
        data,
        isFetching: false,
        isLoading: false,
        expiresAt: expiresInMilliseconds ? dayjs().add(expiresInMilliseconds, 'milliseconds') : undefined
      }
    }));
  }, []);

  const getCache = useCallback((key: string) => {
    return cache[key];
  }, [cache]);

  const fetchData = useCallback((
    key: string,
    fetcher: () => Promise<any>,
    firstRun?: boolean,
    expiresInMilliseconds?: number,
  ) => {
    if (!firstRun) {
      setCache(prev => ({
        ...prev,
        [key]: {
          data: null,
          isLoading: false,
          isFetching: true,
        }
      }));
    }

    fetcher()
      .then(data => {
        updateCache(key, data, expiresInMilliseconds)
        console.log('updated')
      });
  }, [setCache, updateCache]);

  const invalidate = useCallback((key: CacheKeyValues) => {
    console.log('invalidating', key)
    fetchData(key, fetchers[key], true, defaultCacheExpirationMs)
  }, [fetchData, fetchers])

  const isBookmarked = useCallback((id: string) => {
    if (!cache[CACHE_KEYS.BOOKMARKS].data) return undefined;
    return isDocInList(id, cache[CACHE_KEYS.BOOKMARKS].data);
  }, [cache]);
  console.log(cache);
  const isReadLater = useCallback((id: string) => {
    if (!cache[CACHE_KEYS.READ_LATER].data) return undefined;
    return isDocInList(id, cache[CACHE_KEYS.READ_LATER].data);
  }, [cache]);

  // load data effect
  useEffect(() => {
    Object.values(CACHE_KEYS).forEach(key => fetchData(key, fetchers[key], true, defaultCacheExpirationMs))
  }, [fetchData, fetchers]);

  useEffect(() => {
    const checkData = () => {
      Object.values(CACHE_KEYS).forEach(key => {
        if (!cache[key] || cache[key].isFetching || cache[key].isLoading) return;
        if (!cache[key].expiresAt) return;
        if (cache[key].expiresAt?.isBefore(dayjs())) {
          fetchData(key, fetchers[key], false, defaultCacheExpirationMs)
        }
      })
    }
    const interval = setInterval(() => checkData(), 5 * 1000);
    return () => clearInterval(interval);
  }, [cache, fetchData, fetchers])

  const isLoading = useMemo(() => Object.keys(cache).some(e => !!cache[e].isLoading), [cache]);

  return {
    isLoading, cache, set: updateCache, get: getCache, invalidate,
    isBookmarked, isReadLater,
  }
}
